import { createFileRoute, Link } from '@tanstack/react-router'
import { User, CreditCard } from 'lucide-react'

import { type Order } from '@/api/admin-orders-types'
import { preloadGetOrder, useGetOrder } from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { Copy } from '@/components/base/copy'
import { Heading } from '@/components/base/heading'
import { LoadingSpinner } from '@/components/base/loading-spinner'
import { StatusBadge } from '@/components/base/status-badge'
import { Text } from '@/components/base/text'
import { SingleColumnPage } from '@/components/page-layout'
import { OrderDetailSkeleton } from '@/components/skeletons/order-detail-skeleton'
import { useI18n } from '@/i18n/i18n-react'
import { OrderPaymentsSection } from '@/routes/_authenticated/_general/orders/$orderUuid/-components/order-payments'
import { OrderSandStarSection } from '@/routes/_authenticated/_general/orders/$orderUuid/-components/order-sandstar'
import { OrderSummarySection } from '@/routes/_authenticated/_general/orders/$orderUuid/-components/order-summary'

export const Route = createFileRoute('/_authenticated/_general/orders/$orderUuid')({
	component: RouteComponent,
	pendingComponent: OrderDetailSkeleton,
	loader: async ({ context, params: { orderUuid } }) =>
		await preloadGetOrder(context, {
			path: { orderUuid }
		}),
	staticData: {
		crumb: ({ loaderData, context: { L } }) => L.OrderDetail.Title(loaderData?.displayId)
	}
})

function RouteComponent() {
	const { L } = useI18n()
	const { orderUuid } = Route.useParams()
	const { data: order } = useGetOrder({ orderUuid })

	if (!order) {
		return <LoadingSpinner centered />
	}

	return (
		<SingleColumnPage hasOutlet>
			<Container className='flex items-center justify-between'>
				<ContainerHeader
					heading={
						<div className='flex items-center gap-x-1'>
							<Heading>#{order.displayId}</Heading>
							<Copy content={`#${order.displayId}`} className='text-ui-fg-muted' />
						</div>
					}
					subHeading={
						<div className='flex flex-col gap-y-1'>
							<Text size='small' className='text-ui-fg-subtle'>
								{order.pointOfSale
									? L.OrderDetail.OnDateFromPointOfSale({
											date: L.Date.DateAndTime(order.date),
											salesChannel: order.salesChannel,
											pointOfSale: order.pointOfSale
										})
									: L.OrderDetail.OnDateFromSalesChannel({
											date: L.Date.DateAndTime(order.date),
											salesChannel: order.salesChannel
										})}
							</Text>
							{(order.customer || order.loyaltyCard) && (
								<div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
									{order.customer && (
										<Link
											to='/customers/$customerUuid'
											params={{ customerUuid: order.customer.uuid }}
											className='inline-flex items-center gap-x-1 text-ui-fg-subtle hover:text-ui-fg-base hover:underline'
										>
											<User size={13} strokeWidth={2} />
											<Text size='small'>
												{formatCustomerName(order.customer, L.Customers.Anonymous())}
											</Text>
										</Link>
									)}
									{order.loyaltyCard && (
										<Link
											to='/loyalty-cards/$cardUuid'
											params={{ cardUuid: order.loyaltyCard.uuid }}
											className='inline-flex items-center gap-x-1 text-ui-fg-subtle hover:text-ui-fg-base hover:underline'
										>
											<CreditCard size={13} strokeWidth={2} />
											<Text size='small'>{order.loyaltyCard.cardNumber}</Text>
										</Link>
									)}
								</div>
							)}
						</div>
					}
					rightSide={
						<div className='flex items-center gap-x-4'>
							<div className='flex items-center gap-x-1.5'>
								<StatusBadge color='green' className='text-nowrap'>
									{L.Enum.OrderStatus(order.status)}
								</StatusBadge>
							</div>
						</div>
					}
				/>
			</Container>
			<OrderSummarySection order={order} />
			<OrderPaymentsSection order={order} />
			<OrderSandStarSection order={order} />
		</SingleColumnPage>
	)
}

const formatCustomerName = (customer: NonNullable<Order['customer']>, anonymousFallback: string): string => {
	const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ')
	return name || customer.email || customer.phone || anonymousFallback
}
